import { Lock } from "lucide-react";
import { useTranslation } from "../../i18n";
import type { AccessControlConfig } from "./types";

// Settings › Access Control › Fixed rules — what the matrix cannot override. Read-only; the list
// comes from the BE (app/accessControl/fixedRules.js), which a test keeps identical to the code.
const tr = (t: (k: string) => string, key: string, fallback: string) => {
    const v = t(key);
    return v === key ? fallback : v;
};

const FixedRulesView = ({ config }: { config: AccessControlConfig }) => {
    const { t } = useTranslation();
    const floors = config.fixedRules?.floors ?? [];
    const guards = config.fixedRules?.guards ?? [];
    const keyLabel = (key: string) => config.keys.find(k => k.key === key)?.label ?? key;

    return (
        <div className="ac-fixed-rules">
            <p className="ac-hint">{t("ac_fixed_intro")}</p>

            <h3 className="ac-fixed-heading"><Lock size={16} strokeWidth={2} aria-hidden="true" /> {t("ac_fixed_floors_title")}</h3>
            <p className="ac-hint">{t("ac_fixed_floors_hint")}</p>
            <div className="ac-matrix-scroll">
                <table className="ac-matrix-table">
                    <thead>
                        <tr>
                            <th>{t("ac_fixed_col_action")}</th>
                            <th>{t("ac_fixed_col_who")}</th>
                            <th>{t("ac_fixed_col_row")}</th>
                            <th>{t("ac_fixed_col_why")}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {floors.map(f => (
                            <tr key={f.id}>
                                <td>
                                    {tr(t, `ac_floor_${f.id}`, f.label)}
                                    <div className="ac-fixed-routes">{f.routes.map(r => <code key={r} className="ac-key-code">{r}</code>)}</div>
                                </td>
                                <td>{t(f.floor === "Admin" ? "ac_fixed_admin_only" : "ac_fixed_manager_admin")}</td>
                                <td>{keyLabel(f.key)}<div><code className="ac-key-code">{f.key}</code></div></td>
                                <td>{tr(t, `ac_floor_why_${f.id}`, f.why)}</td>
                            </tr>
                        ))}
                        {floors.length === 0 && <tr><td colSpan={4} className="ac-hint">{t("ac_fixed_none")}</td></tr>}
                    </tbody>
                </table>
            </div>

            <h3 className="ac-fixed-heading"><Lock size={16} strokeWidth={2} aria-hidden="true" /> {t("ac_fixed_guards_title")}</h3>
            <ul className="ac-fixed-guards">
                {guards.map(g => <li key={g.id}>{tr(t, `ac_guard_${g.id}`, g.label)}</li>)}
            </ul>
        </div>
    );
};

export default FixedRulesView;
